<template>
  <v-expansion-panels v-model="outerPanel" accordion class="narrow-expansion-panels">
    <v-expansion-panel>
      <v-expansion-panel-header>
        <span class="subtitle-2">
          {{ logs.length }} {{ logs.length === 1 ? $t('log') : $t('logs') }}
        </span>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <v-container fluid class="pa-0">
          <v-row v-if="!useMobileTableStyle" class="ml-2 pl-3 pb-1" style="margin-right: 66px;" no-gutters>
            <v-col cols="auto" :style="{ width: `${logHeaders.find(h => h.value === 'attributes')?.width}px` }">
            </v-col>

            <v-col v-for="(header, i) in logHeadersFiltered.filter(h => h.value !== 'attributes')"
              :cols="header.width ? 'auto' : ''"
              :style="{ width: header.width ? `${header.width}px` : 'auto' }"
              :key="header.value"
              :class="`${header.align === 'right' ? 'text-right' : ''} ${i < logHeadersFiltered.length - 1 ? 'pr-6' : ''}`">
              <span class="body-2 small-table-header">
                {{ header.text }}
              </span>
            </v-col>
          </v-row>

          <v-container fluid class="pa-0"
            :style="panelsContainerStyle">

            <v-expansion-panels
            class="narrow-expansion-panels extra-narrow pb-2"
            accordion
            popout
            v-model="openLogPanel">

              <v-expansion-panel v-for="log in logs" :key="log.logNumber">
                <v-expansion-panel-header :style="{ height: useMobileTableStyle ? '260px' : '' }">
                  <v-row no-gutters align="center" justify="space-between" style="margin-right: 24px;">
                    <v-col cols="auto" :style="{ width: `${logHeaders.find(h => h.value === 'attributes')?.width}px` }">
                      <v-row justify="center">
                        <Icon
                        iconColor="warning"
                        v-if="logUsesBaseRate(log)"
                        icon="baseRateMop"
                        :tooltipText="$t('baseRateUsed')"
                        :small="false"
                        />
                        <Icon
                        iconColor="grey darken-2"
                        v-if="log.buttLog"
                        :icon="'butt-log'"
                        :tooltipText="$t('buttLog')"
                        :small="false"
                        margin=""/>
                      </v-row>
                    </v-col>

                    <v-col
                    v-for="(header, i) in logHeadersFiltered.filter(h => h.value !== 'attributes')"
                    cols="12"
                    :md="header.width ? 'auto' : ''"
                    :key="header.value"
                    :class="`${header.align === 'right' && !useMobileTableStyle ? 'text-right' : ''}
                      ${useMobileTableStyle ? 'py-1' : ''}
                      ${i < logHeadersFiltered.length - 1 ? 'pr-6' : ''}`"
                    :style="{
                      marginBottom: useMobileTableStyle ? '4px' : '',
                      marginTop: useMobileTableStyle ? '4px' : '',
                      width: `${header.width}px`
                    }">
                        <span v-if="useMobileTableStyle" class="font-weight-bold" :style="{ marginRight: useMobileTableStyle ? '8px' : '' }"> {{ header.text }}: </span>
                        <span v-if="header.value === 'grossVolume' || header.value === 'netVolume'">
                          {{ numberWithCommas(log[header.value], 3) }}
                        </span>
                        <span v-else>
                          {{ log[header.value] }}
                        </span>

                        <span v-if="header.value === 'cut'">
                          <v-chip v-for="chip in cutChipsForLog(log)" :key="chip.id" small style="margin-top: 2px; margin-bottom: 2px; margin-right: 4px;">
                            <Icon :icon="chip.icon" :tooltipText="$t(chip.id)" iconColor="grey darken-2"></Icon>
                            <span>
                              {{ chip.text }}
                            </span>
                          </v-chip>
                      </span>
                    </v-col>
                  </v-row>
                </v-expansion-panel-header>

                <v-expansion-panel-content class="grey lighten-4">
                  <v-card outlined class="ma-3" style="overflow: hidden;">
                    <v-data-table
                    id="ticket-scale-data-by-log-segment-table"
                    :style="{
                      borderRadius: useMobileTableStyle ? '4px' : '',
                      margin: useMobileTableStyle ? '12px' : '',
                      overflow: useMobileTableStyle ? 'hidden' : '',
                      border: useMobileTableStyle ? '1px solid #e0e0e0' : ''
                    }"
                    item-key="uuid"
                    single-expand
                    :expanded.sync="expandedSegments"
                    @click:row="rowClicked"
                    class="small-headers"
                    dense
                    hide-default-footer
                    :items-per-page="-1"
                    :items="log.segments"
                    :headers="segmentHeadersGroupedByLog"
                    :customCells="customCells">
                      <template #item.length="{item}">
                        {{ item.length === '-1\'' ? $t('notAvailable') : item.length }}
                      </template>
                      <template #item.diameter="{item}">
                        {{ item.diameter === '-1"' ? $t('notAvailable') : item.diameter }}
                      </template>
                      <template #item.grossVolume="{item}">
                        {{ numberWithCommas(item.grossVolume, 3) }}
                      </template>
                      <template #item.netVolume="{item}">
                        {{ numberWithCommas(item.netVolume, 3) }}
                      </template>
                    </v-data-table>
                  </v-card>

                  <TicketScaleDataFinancials
                  v-if="showTicketScaleDataFinancials"
                  :segmentOrLog="log"
                  :rateSheets="rateSheets"
                  class="px-3 pb-2"
                  @rate-sheet-detail-clicked="rateSheetDetailClicked"/>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-container>
        </v-container>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import { mapActions } from 'vuex'
import { formatMoney, numberWithCommas } from '@/utils/NumericMutations'
import { AccountingCategory } from '@/utils/Enumerations'
import { sumBy, uniq } from 'lodash'
import { baseRateMop } from '@/images/AngelSvgLibrary.js'
export default {
  name: 'TicketScaleDataByLog',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    TicketScaleDataFinancials: () => import('@/components/ticket/ticket-details/scale-data/TicketScaleDataFinancials.vue')
  },

  props: {
    logs: {
      type: Array,
      required: true
    },
    logHeaders: {
      type: Array,
      required: true
    },
    segmentHeadersGroupedByLog: {
      type: Array,
      required: true
    },
    rateSheets: {
      type: Map,
      required: true
    }
  },

  data: () => ({
    baseRateMop,
    openLogPanel: null,
    expandedSegments: [],
    outerPanel: null
  }),

  created () {
    if (this.logs && this.logs.some(this.logUsesBaseRate)) {
      this.outerPanel = 0
    } else {
      this.outerPanel = null
    }
  },

  computed: {
    panelsContainerStyle () {
      return {
        maxHeight: 'calc(90vh - 270px)',
        overflowY: 'auto',
        borderTop: '1px solid',
        borderImage: 'linear-gradient(to right, #fff 2%, #ddd 10%, #ddd 90%, #fff 98%) 1'
      }
    },

    logHeadersFiltered () {
      return this.logHeaders.filter(header => {
        switch (header.value) {
          case 'attributes': {
            return this.logs.some(l => l.buttLog)
          }
          case 'grade': {
            return this.logs.some(l => !!l.grade)
          }
          case 'defectDefinition': {
            return this.logs.some(l => !!l.defectDefinitionId)
          }
          case 'cut': {
            return this.logs.some(l => !!l.defectDefinitionId)
          }
          default: return true
        }
      })
    },

    segmentHeadersGroupedByLogFiltered () {
      return this.segmentHeadersGroupedByLog.filter(header => {
        switch (header.value) {
          case 'grade': {
            return this.logs.some(l => !!l.grade)
          }
          default: return true
        }
      })
    },

    useMobileTableStyle () {
      return this.$vuetify.breakpoint.smAndDown
    },

    showTicketScaleDataFinancials () {
      return this.logs
        .some(log => log.segments
          .some(s => s.amounts && s.amounts.length > 0))
    },

    customCells () {
      return [
        {
          slotName: 'length',
          value: 'length'

        },
        {
          slotName: 'diameter',
          value: 'diameter'
        },
        {
          slotName: 'grossVolume',
          value: 'grossVolume'
        },
        {
          slotName: 'netVolume',
          value: 'netVolume'
        }
      ]
    }
  },

  methods: {
    rateSheetDetailClicked (tableRow) {
      this.$emit('rate-sheet-detail-clicked', {
        tableRow,
        panel: this.openLogPanel
      })
    },
    formatMoney,
    numberWithCommas,
    sumBy,

    ...mapActions('scale-data', ['fetchScaleDataForTicket']),
    ...mapActions('rate-sheet', ['fetchRateSheet']),

    async retrieveRateSheets () {
      const uniqRateSheetIds = uniq(this.scaleData.segmentVolumes
        .flatMap(s => s.amounts
          .flatMap(a => a.rateSheetId)))
        .filter(id => !!id)

      await Promise.all(uniqRateSheetIds
        .map(id => this.fetchRateSheetById(id)))
    },

    async fetchRateSheetById (id) {
      if (id == null) {
        console.warn('Attempted to fetch rate sheet with null or undefined ID.')
        return
      }
      const rateSheet = await this.fetchRateSheet(id)
      this.rateSheets.set(id, {
        ...rateSheet,
        rateSheetLines: rateSheet.rateSheetLines
      })
    },

    getCategoryFromString (categoryString) {
      switch (categoryString) {
        case 'AccountsPayable': return AccountingCategory.Payable.value
        case 'AccountsReceivable': return AccountingCategory.Receivable.value
        case 'JournalEntry': return AccountingCategory.Accrual.value
      }
      return undefined
    },

    cutChipsForLog (log) {
      const chips = []
      if (log.lengthCut) chips.push({ id: 'lengthCut', icon: 'mdi-ruler', text: log.lengthCut })
      if (log.diameterCut) chips.push({ id: 'diameterCut', icon: 'mdi-diameter', text: log.diameterCut })
      if (log.volumeCut) chips.push({ id: 'volumeCut', icon: 'mdi-cube', text: log.volumeCut })
      return chips
    },

    rowClicked (row) {
      if (this.expandedSegments.some(s => s.uuid === row.uuid)) {
        this.expandedSegments = []
      } else {
        if (row.amounts.length > 0) this.expandedSegments = [row]
      }
    },

    logUsesBaseRate (log) {
      return log.segments?.some(s => s.amounts?.some(a => a.rateSheetLineId === -1))
    }
  }
}
</script>
